import React from "react";
import { Trash2, Plus } from "lucide-react";

/**
 * Google recenze — bez API klíče: admin ručně zadává skóre, počet recenzí
 * a několik ukázkových recenzí (rotují na webu po 4 vteřinách). Odkaz na
 * skutečné recenze se nastavuje v Sociální sítě → Google recenze.
 */
export default function GoogleReviewsEditor({ data, onChange }) {
  const reviews = Array.isArray(data.reviews) ? data.reviews : [];

  const setField = (key, value) => onChange({ ...data, [key]: value });
  const setReview = (i, patch) => onChange({ ...data, reviews: reviews.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const removeReview = (i) => onChange({ ...data, reviews: reviews.filter((_, idx) => idx !== i) });
  const addReview = () => onChange({ ...data, reviews: [...reviews, { author: "", text: "", stars: 5 }] });

  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid="section-google-reviews">
      <div className="text-xs uppercase tracking-[0.3em] text-apex-gold/80 mb-1">Google recenze</div>
      <p className="text-xs text-apex-cream/60 mb-4">
        Zobrazuje se jako rámeček pod galerií na hlavní stránce. Odkaz na recenze se řídí polem „Google recenze“ v Sociální sítě.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Hodnocení (z 5)</div>
          <input
            type="text"
            value={data.rating || ""}
            onChange={(e) => setField("rating", e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm"
            data-testid="google-reviews-rating-input"
          />
        </label>
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Počet recenzí</div>
          <input
            type="text"
            value={data.review_count || ""}
            onChange={(e) => setField("review_count", e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm"
            data-testid="google-reviews-count-input"
          />
        </label>
      </div>

      <div className="text-xs text-apex-cream/70 mb-1.5">Ukázkové recenze (rotují po 4 vteřinách)</div>
      <div className="space-y-3">
        {reviews.map((r, i) => (
          <div key={i} className="rounded-xl border border-apex-line bg-apex-bg/50 p-3 space-y-2" data-testid={`google-review-row-${i}`}>
            <div className="flex gap-2">
              <input
                type="text"
                value={r.author || ""}
                onChange={(e) => setReview(i, { author: e.target.value })}
                placeholder="Jméno recenzenta"
                className="flex-1 px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm"
                data-testid={`google-review-author-${i}`}
              />
              <select
                value={r.stars || 5}
                onChange={(e) => setReview(i, { stars: parseInt(e.target.value, 10) })}
                className="px-2 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm"
                data-testid={`google-review-stars-${i}`}
              >
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>{n} ★</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeReview(i)}
                className="px-2 py-1.5 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10"
                data-testid={`google-review-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <textarea
              value={r.text || ""}
              rows={2}
              onChange={(e) => setReview(i, { text: e.target.value })}
              placeholder="Text recenze"
              className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm resize-y"
              data-testid={`google-review-text-${i}`}
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addReview}
        className="mt-3 text-xs px-3 py-1.5 rounded-lg border border-apex-gold/40 text-apex-gold hover:bg-apex-gold/10 inline-flex items-center gap-1.5"
        data-testid="google-review-add"
      >
        <Plus className="w-3.5 h-3.5" /> Přidat recenzi
      </button>
    </div>
  );
}
